import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "@/state/hooks";
import {
    classifyContextMeshProvenance,
    ContextMeshApiError,
    fetchContextMeshAnalysis,
    type ContextMeshAnalysis,
    type ContextMeshAnalysisResponse
} from "./contextMeshAnalysis";

export type MeshStrengthStatus = "ready" | "stale" | "analyzing" | "unavailable";

export interface MeshStrengthData {
    analysis: ContextMeshAnalysis | null;
    status: MeshStrengthStatus;
    loading: boolean;
    error: ContextMeshApiError | null;
    retry: () => void;
}

const FAST_ANALYSIS_POLL_INTERVAL_MS = 2_000;
const SLOW_ANALYSIS_POLL_INTERVAL_MS = 10_000;
const FAST_ANALYSIS_POLL_ATTEMPTS = 15;
// Share only active network requests. Resolved analysis must not survive a
// remount because the user may have changed this product on another surface.
const analysisRequests = new Map<string, Promise<ContextMeshAnalysisResponse>>();

export function __clearMeshStrengthCache(): void {
    analysisRequests.clear();
}

function cacheKey(userId: number, projectId: number): string {
    return `${userId}:${projectId}`;
}

function getAnalysis(token: string, userId: number, projectId: number): Promise<ContextMeshAnalysisResponse> {
    const key = cacheKey(userId, projectId);
    const existing = analysisRequests.get(key);
    if (existing) return existing;
    const request = fetchContextMeshAnalysis(token, projectId).finally(() => {
        if (analysisRequests.get(key) === request) analysisRequests.delete(key);
    });
    analysisRequests.set(key, request);
    return request;
}

export function useMeshStrengthData(projectId: number | null): MeshStrengthData {
    const token = useAppSelector(state => state.clientToken.token);
    const userId = useAppSelector(state => state.userProfiles.userProfile?.userid);
    const [analysis, setAnalysis] = useState<ContextMeshAnalysis | null>(null);
    const [status, setStatus] = useState<MeshStrengthStatus>("unavailable");
    const [loading, setLoading] = useState(projectId != null);
    const [error, setError] = useState<ContextMeshApiError | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (projectId == null) {
            setAnalysis(null);
            setStatus("unavailable");
            setError(null);
            setLoading(false);
            return;
        }
        setAnalysis(current => (current?.productId === projectId ? current : null));
        if (!token || userId == null) {
            setLoading(true);
            return;
        }
        let cancelled = false;
        let pollTimer: ReturnType<typeof setTimeout> | null = null;
        let pollAttempts = 0;
        setLoading(true);
        setError(null);

        const load = (): void => {
            getAnalysis(token, userId, projectId)
                .then(result => {
                    if (cancelled) return;
                    const classified = classifyContextMeshProvenance(result);
                    setAnalysis(classified.analysis);
                    setStatus(
                        classified.state === "current_canonical"
                            ? "ready"
                            : classified.state === "prior_canonical"
                              ? "stale"
                              : classified.state
                    );
                    setLoading(false);
                    const semanticShouldPoll = result.semanticAnalysis.status === "pending";
                    const categoryShouldPoll = classified.analysis?.categoryAnalysis?.status === "pending";
                    const shouldPoll = semanticShouldPoll || categoryShouldPoll;
                    if (shouldPoll) {
                        const interval = pollAttempts < FAST_ANALYSIS_POLL_ATTEMPTS
                            ? FAST_ANALYSIS_POLL_INTERVAL_MS
                            : SLOW_ANALYSIS_POLL_INTERVAL_MS;
                        pollAttempts += 1;
                        pollTimer = setTimeout(load, interval);
                    }
                })
                .catch(reason => {
                    if (cancelled) return;
                    setAnalysis(null);
                    setStatus("unavailable");
                    setError(
                        reason instanceof ContextMeshApiError
                            ? reason
                            : new ContextMeshApiError(503, { code: "ANALYSIS_UNAVAILABLE", message: "Context analysis is temporarily unavailable." })
                    );
                    setLoading(false);
                });
        };

        load();
        return () => {
            cancelled = true;
            if (pollTimer) clearTimeout(pollTimer);
        };
    }, [projectId, token, userId, attempt]);

    const retry = useCallback(() => {
        if (projectId != null && userId != null) analysisRequests.delete(cacheKey(userId, projectId));
        setAttempt(value => value + 1);
    }, [projectId, userId]);

    return { analysis, status, loading, error, retry };
}
